// src/context/AuthContext.js — Global auth state provider
import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { STORAGE_KEYS } from '../config/env';
import { isTokenExpired } from '../shared/utils/jwt';
import { resetMasterData } from '../features/tickets/services/masterDataService';

const KEY = STORAGE_KEYS.auth;

function readStoredAuth() {
  try {
    const local = localStorage.getItem(KEY);
    if (local) {
      const parsed = JSON.parse(local);
      if (parsed?.token) {
        if (!isTokenExpired(parsed.token)) return parsed;
        clearStoredAuth();
        return null;
      }
      localStorage.removeItem(KEY);
    }
    const session = sessionStorage.getItem(KEY);
    if (session) {
      const parsed = JSON.parse(session);
      if (parsed?.token) {
        if (!isTokenExpired(parsed.token)) return parsed;
        clearStoredAuth();
        return null;
      }
      sessionStorage.removeItem(KEY);
    }
  } catch (e) { /* ignore */ }
  return null;
}

function clearStoredAuth() {
  localStorage.removeItem(KEY);
  sessionStorage.removeItem(KEY);
}

export function getStoredToken() {
  const auth = readStoredAuth();
  return auth?.token || null;
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(readStoredAuth);
  const [sessionExpired, setSessionExpired] = useState(false);

  const login = useCallback(({ user, token, remember }) => {
    // Drop master data from any previous session/user before the new token is
    // persisted, so authenticated screens always load fresh data with it.
    resetMasterData();
    const session = {
      user,
      token,
      signedInAt: new Date().toISOString(),
      remember,
      auth_method: user?.auth_method ?? 'password',
    };
    try {
      if (remember) {
        localStorage.setItem(KEY, JSON.stringify(session));
        sessionStorage.removeItem(KEY);
      } else {
        sessionStorage.setItem(KEY, JSON.stringify(session));
        localStorage.removeItem(KEY);
      }
    } catch (e) { /* ignore */ }
    setAuth(session);
    setSessionExpired(false);
  }, []);

  const logout = useCallback(() => {
    resetMasterData();
    try {
      localStorage.removeItem(KEY);
      sessionStorage.removeItem(KEY);
    } catch (e) { /* ignore */ }
    setAuth(null);
  }, []);

  // Listen for token expiry from API interceptor
  useEffect(() => {
    const handler = () => { logout(); setSessionExpired(true); };
    window.addEventListener('ism:unauthorized', handler);
    return () => window.removeEventListener('ism:unauthorized', handler);
  }, [logout]);

  const contextValue = useMemo(
    () => ({ auth, login, logout, sessionExpired }),
    [auth, login, logout, sessionExpired],
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
}
